import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react';

export type Langue = 'fr' | 'en';

/** Un texte dans les deux langues. Les avoir côte à côte rend l'oubli visible. */
export type Bilingue = { fr: string; en: string };

const CLEF = 'assimil-admin-langue';

/**
 * La langue de l'interface.
 *
 * Le back-office mélangeait les deux — « Done » à côté de « Résolu
 * automatiquement » — parce que chaque écran avait été écrit dans la langue du
 * moment. Le choix se fait désormais en haut, et se retient.
 *
 * Pas de bibliothèque : deux langues et cent cinquante chaînes ne justifient
 * pas une dépendance de plus, ni le découpage en fichiers par langue. Les deux
 * versions se lisent côte à côte, ce qui rend une traduction manquante
 * impossible à écrire.
 */
const Contexte = createContext<{ langue: Langue; choisir: (l: Langue) => void }>({
    langue: 'fr',
    choisir: () => undefined,
});

const langueInitiale = (): Langue => {
    const retenue = localStorage.getItem(CLEF);
    if (retenue === 'fr' || retenue === 'en') return retenue;
    // À défaut, celle du navigateur — l'équipe est française, mais le
    // back-office s'ouvre aussi ailleurs.
    return navigator.language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
};

export const FournisseurDeLangue = ({children}: { children: ReactNode }) => {
    const [langue, setLangue] = useState<Langue>(langueInitiale);

    const choisir = useCallback((choisie: Langue) => {
        setLangue(choisie);
        localStorage.setItem(CLEF, choisie);
    }, []);

    useEffect(() => {
        document.documentElement.lang = langue;
    }, [langue]);

    const valeur = useMemo(() => ({langue, choisir}), [langue, choisir]);
    return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
};

export const useLangue = () => useContext(Contexte);

/**
 * Rend un texte bilingue dans la langue courante.
 *
 * `const t = useTraduire(); t(T.tableauDeBord)` — et pour les tables de
 * libellés : `t(TAG_LABELS[tag])`.
 */
export const useTraduire = () => {
    const {langue} = useLangue();
    return useCallback((texte: Bilingue | undefined) => texte?.[langue] ?? '', [langue]);
};
